import React, { useState, useEffect, useRef } from 'react';
import './SecurityDocuments.css';
import fiatLogo from '../../../img/Logo_Fiat.webp';
import peugeotLogo from '../../../img/Logo_Peugeot.webp';
import renaultLogo from '../../../img/Logo_Renault.webp';
import johnDeer from '../../../img/Logo_John_Deer.webp';
import voiture from '../../../img/Icone-voiture.svg';
import camion from '../../../img/Icone-camion.svg';
import moto from '../../../img/Icone-moto.svg';
import caravane from '../../../img/Icone-caravane.svg';
import agricole from '../../../img/Icone-engin-agricole.svg';
import campingCar from '../../../img/Icone-camping-car.svg';
import { Link } from 'react-router-dom';

const SecurityDocuments = () => {
  // Only maintain positions, no need for separate index variables
  const [brandPosition, setBrandPosition] = useState(0);
  const [vehiclePosition, setVehiclePosition] = useState(0);
  const brandsTrackRef = useRef(null);
  const vehiclesTrackRef = useRef(null);

  const vehicles = [
    {
      src: voiture,
      alt: "Icone de voiture",
      description: "Icone de voiture"
    },
    {
      src: camion,
      alt: "Icone de camion",
      description: "Icone de camion"
    },
    {
      src: moto,
      alt: "Icone de moto",
      description: "Icone de moto"
    },
    {
      src: caravane,
      alt: "Icone de caravane",
      description: "Icone de caravane"
    },
    {
      src: agricole,
      alt: "Icone de agricole",
      description: "Icone de agricole"
    },
    {
      src: campingCar,
      alt: "Icone de camping car",
      description: "Icone de camping car"
    }
  ];

  const brands = [
    {
      src: fiatLogo,
      alt: "Logo Fiat - Constructeur automobile italien",
      description: "Constructeur automobile italien fondé en 1899"
    },
    {
      src: peugeotLogo,
      alt: "Logo Peugeot - Constructeur automobile français",
      description: "Constructeur automobile français fondé en 1896"
    },
    {
      src: renaultLogo,
      alt: "Logo Renault - Constructeur automobile français",
      description: "Constructeur automobile français fondé en 1898"
    },
    {
      src: johnDeer,
      alt: "Logo John Deere - Constructeur agricole américain",
      description: "Constructeur agricole américain fondé en 1837"
    },
  ];

  // For true infinite scroll, create extended array with duplicates
  const getCarouselItems = (items) => {
    // Duplicate the entire array to allow for continuous rightward scrolling
    // This creates a buffer that allows for a seamless infinite loop
    return [...items, ...items, ...items];
  };

  // Initialize carousel positions
  useEffect(() => {
    // Start from the middle set (second copy) to allow infinite scrolling in both directions
    const startingIndex = brands.length;
    setBrandPosition(startingIndex);
    setVehiclePosition(startingIndex);
    
    if (brandsTrackRef.current) {
      brandsTrackRef.current.style.transform = `translateX(-${startingIndex * 33.33}%)`;
    }
    if (vehiclesTrackRef.current) {
      vehiclesTrackRef.current.style.transform = `translateX(-${startingIndex * 33.33}%)`;
    }
  }, [brands.length, vehicles.length]);

  // Preload all brand logo images
  useEffect(() => {
    brands.forEach(brand => {
      const img = new Image();
      img.src = brand.src;
    });
  }, []);

  // Auto slide for brand logos with true infinite scroll to the right
  useEffect(() => {
    const totalItems = brands.length * 3; // We have 3 copies of the items
    
    const brandInterval = setInterval(() => {
      // Always move forward (to the right)
      const nextPosition = brandPosition + 1;
      setBrandPosition(nextPosition);
      
      if (brandsTrackRef.current) {
        // Use a smoother easing and slightly longer transition
        brandsTrackRef.current.style.transition = 'transform 1s cubic-bezier(0.215, 0.61, 0.355, 1)';
        brandsTrackRef.current.style.transform = `translateX(-${nextPosition * 33.33}%)`;
        
        // Reset much earlier - when we're 2/3 through the last set
        // This gives more time for the reset to happen invisibly
        if (nextPosition >= totalItems - brands.length) {
          // After transition completes, quickly reset to equivalent position in middle set
          setTimeout(() => {
            if (brandsTrackRef.current) {
              // Remove transition to instantly jump without animation
              brandsTrackRef.current.style.transition = 'none';
              // Move back to the equivalent position in the middle set
              const resetPosition = brands.length + (nextPosition % brands.length);
              brandsTrackRef.current.style.transform = `translateX(-${resetPosition * 33.33}%)`;
              setBrandPosition(resetPosition);
            }
          }, 1000); // Match the transition duration
        }
      }
    }, 3000);

    return () => clearInterval(brandInterval);
  }, [brandPosition, brands.length]);

  // Preload all vehicle icon images
  useEffect(() => {
    vehicles.forEach(vehicle => {
      const img = new Image();
      img.src = vehicle.src;
    });
  }, []);

  // Auto slide for vehicle icons with true infinite scroll to the right
  useEffect(() => {
    const totalItems = vehicles.length * 3; // We have 3 copies of the items
    
    const vehicleInterval = setInterval(() => {
      // Always move forward (to the right)
      const nextPosition = vehiclePosition + 1;
      setVehiclePosition(nextPosition);
      
      if (vehiclesTrackRef.current) {
        // Use a smoother easing and slightly longer transition
        vehiclesTrackRef.current.style.transition = 'transform 1s cubic-bezier(0.215, 0.61, 0.355, 1)';
        vehiclesTrackRef.current.style.transform = `translateX(-${nextPosition * 33.33}%)`;
        
        // Reset much earlier - when we're 2/3 through the last set
        // This gives more time for the reset to happen invisibly
        if (nextPosition >= totalItems - vehicles.length) {
          // After transition completes, quickly reset to equivalent position in middle set
          setTimeout(() => {
            if (vehiclesTrackRef.current) {
              // Remove transition to instantly jump without animation
              vehiclesTrackRef.current.style.transition = 'none';
              // Move back to the equivalent position in the middle set
              const resetPosition = vehicles.length + (nextPosition % vehicles.length);
              vehiclesTrackRef.current.style.transform = `translateX(-${resetPosition * 33.33}%)`;
              setVehiclePosition(resetPosition);
            }
          }, 1000); // Match the transition duration
        }
      }
    }, 4000);

    return () => clearInterval(vehicleInterval);
  }, [vehiclePosition, vehicles.length]);

  // This initialization has been replaced by the one at the top

  return (
    <section id="security-documents" className="security-documents">
      <div className="security-documents-content">
        <h3>Documents administratifs tous véhicules</h3>
        
        {/* Smooth vehicles carousel */}
        <div className="vehicle-carousel">
          <div className="carousel-track" ref={vehiclesTrackRef}>
            {getCarouselItems(vehicles).map((vehicle, index) => (
              <div 
                key={`vehicle-${index}`} 
                className="carousel-item"
              >
                <img
                  src={vehicle.src}
                  alt={vehicle.alt}
                  title={vehicle.description}
                  className="vehicle-icon"
                />
              </div>
            ))}
          </div>
        </div>

        <p>VéhiDoc vous accompagne dans les démarches administratifs de tous vos véhicules :</p>
        <p>voiture motorisée et électrique, véhicule utilitaire, moto et scooter, poids lourd,
          engin agricole, camping-car, quad, tricycle motorisé, autocar et autobus, remorque et caravane.
        </p>
      </div>
      <div className="security-documents-button-container">
        <Link to="/contact" className="security-documents-button">Nous contacter</Link>
      </div>
      <div className="security-documents-image">
        <h4>Leurs documents en toute sécurité</h4>
        
        {/* Smooth brands carousel */}
        <div className="brand-carousel">
          <div className="carousel-track" ref={brandsTrackRef}>
            {getCarouselItems(brands).map((brand, index) => (
              <div 
                key={`brand-${index}`} 
                className="carousel-item"
              >
                <img
                  src={brand.src}
                  alt={brand.alt}
                  title={brand.description}
                  className="brand-logo"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecurityDocuments;